import React, { useState, useEffect } from 'react'
import Sheet from '@amzn/meridian/sheet'
import SearchField from '@amzn/meridian/search-field'
import Button from '@amzn/meridian/button'
import Icon from '@amzn/meridian/icon'
import closeSmallTokens from '@amzn/meridian-tokens/base/icon/close-small'
import Heading from '@amzn/meridian/heading'
import Column from '@amzn/meridian/column'
import Row from '@amzn/meridian/row'
import Box from '@amzn/meridian/box'
import Divider from '@amzn/meridian/divider'
import { IStoreFilterSheetProps } from '../types'
import { FILTER_NOTIFICATIONS_HEADING, APPLY_FILTERS_LABEL, SEARCH_STORES_PLACEHOLDER } from '../constants'
import { StoreFilterItem } from './StoreFilterItem'
import { EmptyState } from '@pages/StoreSelectionPage/components/EmptyState'
import { filterStores } from '@pages/StoreSelectionPage/utils/filterStores'
import { useBundle } from '@amzn/react-arb-tools'
import { BUNDLE_NAME } from '@src/constants'
import { t } from '@src/translationHelper'
import './StoreFilterSheet.scss'

/**
 * StoreFilterSheet Component
 *
 * A bottom overlay sheet for multi-select store filtering.
 * Modeled after StoreSelectionSheet in structure and styling.
 * Manages temporary selection state locally — discarded on close without apply.
 */
export const StoreFilterSheet: React.FC<IStoreFilterSheetProps> = ({
    open,
    onClose,
    stores,
    initialSelectedStoreIds,
    onApply,
}) => {
    const [tempSelectedStoreIds, setTempSelectedStoreIds] = useState<string[]>(initialSelectedStoreIds)
    const [searchQuery, setSearchQuery] = useState('')
    const [bundle] = useBundle(BUNDLE_NAME)

    // Re-initialize local state from props whenever the sheet opens
    useEffect(() => {
        if (open) {
            setTempSelectedStoreIds(initialSelectedStoreIds)
            setSearchQuery('')
        }
    }, [open, initialSelectedStoreIds])

    const filteredStores = filterStores(stores, searchQuery)

    const handleToggleSelect = (storeId: string) => {
        setTempSelectedStoreIds((prev) =>
            prev.includes(storeId)
                ? prev.filter((id) => id !== storeId)
                : [...prev, storeId],
        )
    }

    const handleApply = () => {
        onApply(tempSelectedStoreIds)
        onClose()
    }

    const handleSearchChange = (query: string) => {
        setSearchQuery(query)
    }

    const handleSearchSubmit = (query: string) => {
        (document.activeElement as HTMLElement)?.blur()
        setSearchQuery(query)
    }

    const handleSearchClear = () => {
        setSearchQuery('')
    }

    return (
        <Sheet
            type="overlay"
            side="bottom"
            open={open}
            onClose={onClose}
            spacingInset="none"
            data-atb-id="store-filter-sheet"
        >
            <div className="store-filter-sheet-content">
                <Column spacing="none" height="100%">
                    <Row alignmentHorizontal="justify" alignmentVertical="center" spacingInset="400 400 none 400">
                        <Heading level={5}>{t(bundle, FILTER_NOTIFICATIONS_HEADING)}</Heading>
                        <Button
                            type="icon"
                            size="small"
                            onClick={onClose}
                            data-testid="close-store-filter-sheet"
                        >
                            <Icon tokens={closeSmallTokens}>{t(bundle, 'jsma-web-common-close')}</Icon>
                        </Button>
                    </Row>
                    <Column spacingInset="300 400 400 400">
                        <Box type="fill" spacingInset="300 400">
                            <SearchField
                                value={searchQuery}
                                onChange={handleSearchChange}
                                onSubmit={handleSearchSubmit}
                                onClear={handleSearchClear}
                                placeholder={t(bundle, SEARCH_STORES_PLACEHOLDER)}
                                searchButton={true}
                            />
                        </Box>
                    </Column>
                    {filteredStores.length === 0 ? (
                        <EmptyState />
                    ) : (
                        <div className="store-filter-sheet-list">
                            {filteredStores.map((store) => (
                                <React.Fragment key={store.storeId}>
                                    <StoreFilterItem
                                        store={store}
                                        isSelected={tempSelectedStoreIds.includes(store.storeId)}
                                        onToggle={handleToggleSelect}
                                    />
                                    <Divider size="small" />
                                </React.Fragment>
                            ))}
                        </div>
                    )}
                    {(tempSelectedStoreIds.length > 0 || initialSelectedStoreIds.length > 0) && (
                        <div className="apply-filters-button-container primary-theme-button">
                            <Button
                                onClick={handleApply}
                                data-testid="apply-filters-button"
                            >
                                {t(bundle, APPLY_FILTERS_LABEL, { filterCount: tempSelectedStoreIds.length })}
                            </Button>
                        </div>
                    )}
                </Column>
            </div>
        </Sheet>
    )
}

export default StoreFilterSheet
